import type { RichTextContent } from '../../types/contentstack'
import type { FaqsFields } from './types'

type RteMark = { bold?: boolean; italic?: boolean; underline?: boolean }

const text = (value: string, marks: RteMark = {}) => ({ text: value, ...marks })

const link = (
  uid: string,
  href: string,
  children: Array<{ text: string; bold?: boolean; italic?: boolean; underline?: boolean }>,
) => ({
  type: 'a',
  uid,
  attrs: { url: href },
  children,
})

const paragraph = (
  uid: string,
  children: Array<
    | { text: string; bold?: boolean; italic?: boolean; underline?: boolean }
    | ReturnType<typeof link>
  >,
) => ({
  type: 'p',
  uid,
  attrs: {},
  children,
})

const doc = (uid: string, children: ReturnType<typeof paragraph>[]): RichTextContent =>
  ({
    type: 'doc',
    uid,
    attrs: {},
    children,
  } as unknown as RichTextContent)

export const faqsMockData: FaqsFields = {
  heading: 'Gousto food boxes FAQs',
  body: [
    {
      title: 'Is a Gousto food box actually worth it?',
      description: doc('faq-0-doc', [
        paragraph('faq-0-p1', [
          text(
            'Yes, chef! You simply pick the recipes you love to arrive on a day that suits you, and we send all the top-quality, perfectly pre-portioned ingredients straight to your door. No mealtime mayhem, no food waste and no shopping stress - just tasty meals made easy.',
          ),
        ]),
      ]),
    },
    {
      title: 'Do I have to get a food box every single week?',
      description: doc('faq-1-doc', [
        paragraph('faq-1-p1', [
          text(
            'Nope. Our food box subscription is super flexible. You can pause, skip or stop deliveries anytime from the app or website. you’re totally in charge. No awkward break-up needed.',
          ),
        ]),
      ]),
    },
    {
      title: 'What does a Gousto food box cost?',
      description: doc('faq-2-doc', [
        paragraph('faq-2-p1', [
          text(
            'The price of your recipe box depends on the number of recipes you choose and how many people you’re catering for. The more you order, the less you’ll pay per portion, with prices starting at £3.09 per portion. If you’d like more information on prices, check out our guide to see how much a ',
          ),
          link('faq-2-link', '/box-prices', [text('recipe box costs.', { bold: true })]),
          text(' '),
        ]),
      ]),
    },
    {
      title: 'Are Gousto food boxes sustainable?',
      description: doc('faq-3-doc', [
        paragraph('faq-3-p1', [
          text(
            'Very. From recyclable boxes and smart packaging to clever portioning that cuts down on waste, we’re all about good food that’s good for the planet, too.',
          ),
        ]),
      ]),
    },
    {
      title: 'Do you deliver to my house?',
      description: doc('faq-4-doc', [
        paragraph('faq-4-p1', [
          text(
            'We deliver to any UK or Republic of Ireland address you fancy - home, office, treehouse (okay, maybe not that last one, but you get the idea).',
          ),
        ]),
      ]),
    },
    {
      title: 'Can I take a break from food box deliveries?',
      description: doc('faq-5-doc', [
        paragraph('faq-5-p1', [
          text(
            'Absolutely. Skip a week, pause your subscription—whatever suits you. Do it all with a few taps from your device, no drama.',
          ),
        ]),
      ]),
    },
    {
      title: 'Why is Gousto the best food box in the UK?',
      description: doc('faq-6-doc', [
        paragraph('faq-6-p1', [
          text(
            'We’ve got the biggest menu, the best value, and top-notch ingredients that make every meal a winner. Basically, if dinner was a competition, you’d be nailing it.',
          ),
        ]),
      ]),
    },
    {
      title: 'What size boxes does Gousto have?',
      description: doc('faq-7-doc', [
        paragraph('faq-7-p1', [
          text(
            'Did you know that we’re the first recipe box in Britain to serve 1-5 people? Our food boxes are made to suit a number of households, as we understand that every home looks different. Whether you’re a busy family of 5, a couple looking to shake up their dinners, or you live alone and want to cut down on food waste, there’s a box for you.',
          ),
        ]),
      ]),
    },
  ],
}
